import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type {
  AccountSessionResponse,
  AccountUser,
  AuthProvider as AuthProviderName,
  AuthSession,
  CompanyWorkspace,
  CreateWorkspaceRequest,
  WorkspaceContract,
} from '@nest-react/domain';
import type { AppLocale } from '@nest-react/lib';
import { i18n } from './i18n';
import { useRegionalApi } from './region';
import { requestWithActiveRegionalClient } from './regionalApiClient';
import { useWorkspaceIdentitySync } from '~/stores/workspace';

const LOCALE_KEY = 'mylo-studio:locale';

const initialLocale = (): AppLocale => {
  const stored = localStorage.getItem(LOCALE_KEY);
  if (stored === 'en' || stored === 'ru' || stored === 'zh-Hans') return stored;
  const language = navigator.language.toLowerCase();
  if (language.startsWith('ru')) return 'ru';
  if (language.startsWith('zh')) return 'zh-Hans';
  return 'en';
};

const toCompany = (workspace: WorkspaceContract): CompanyWorkspace => ({
  id: workspace.id,
  name: workspace.name,
  website: workspace.profile.website,
  industryCode: workspace.profile.industryCode,
  companySizeCode: workspace.profile.companySizeCode,
  jobTitle: workspace.jobTitle,
  primaryMarketCode: workspace.profile.primaryMarketCode,
  dataRegion: workspace.dataRegion,
});

interface AccountValue {
  account: AccountSessionResponse | null;
  session: AuthSession | null;
  ready: boolean;
  refresh: () => Promise<void>;
  updateUser: (user: AccountUser) => void;
  signIn: (provider: AuthProviderName, returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AccountContext = createContext<AccountValue | null>(null);

export const AuthProvider = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const api = useRegionalApi();
  const [account, setAccount] = useState<AccountSessionResponse | null>(null);
  const [ready, setReady] = useState(false);
  const refresh = useCallback(async () => {
    try {
      const next = await api.request<AccountSessionResponse>(
        '/auth/session/current'
      );
      setAccount(next);
    } catch {
      setAccount(null);
    } finally {
      setReady(true);
    }
  }, [api]);
  useEffect(() => {
    localStorage.removeItem('mylo-studio:v1');
    const timer = window.setTimeout(() => {
      refresh().catch(() => undefined);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);
  const session = useMemo<AuthSession | null>(() => {
    if (!account) return null;
    return {
      accessToken: '',
      user: {
        id: account.user.id,
        email: account.user.email,
        displayName: account.user.displayName,
        avatarUrl: account.user.avatarUrl,
        authProvider: account.user.providers[0] || 'google',
        locale: account.user.locale,
      },
    };
  }, [account]);
  const value = useMemo<AccountValue>(
    () => ({
      account,
      session,
      ready,
      refresh,
      updateUser: user => {
        setAccount(current => (current ? { ...current, user } : current));
      },
      signIn: async (provider, returnTo = '/') => {
        window.location.assign(api.oauthUrl(provider, returnTo));
      },
      signOut: async () => {
        await api.request<{ ok: true }>('/auth/logout', { method: 'POST' });
        setAccount(null);
      },
    }),
    [account, api, ready, refresh, session]
  );
  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
};

export const useAuth = (): AccountValue => {
  const value = useContext(AccountContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
};

interface WorkspaceValue {
  companies: CompanyWorkspace[];
  workspaceContracts: WorkspaceContract[];
  activeCompany: CompanyWorkspace | null;
  activeWorkspace: WorkspaceContract | null;
  projects: {
    id: string;
    name: string;
    description: string;
    channel: string;
    status: 'draft' | 'active' | 'review';
    assetCount: number;
  }[];
  assets: { id: string }[];
  locale: AppLocale;
  createCompany: (company: CreateWorkspaceRequest) => Promise<CompanyWorkspace>;
  createInitialCompany: (
    company: CreateWorkspaceRequest
  ) => Promise<CompanyWorkspace>;
  setActiveCompany: (id: string) => Promise<void>;
  setLocale: (locale: AppLocale) => Promise<void>;
  refreshWorkspaces: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export const ActiveWorkspaceProvider = ({
  children,
}: React.PropsWithChildren): React.ReactElement => {
  const api = useRegionalApi();
  const { account, refresh, updateUser } = useAuth();
  const [anonymousLocale, setAnonymousLocale] =
    useState<AppLocale>(initialLocale);
  const [pendingLocale, setPendingLocale] = useState<AppLocale | null>(null);
  const workspaceContracts = account?.workspaces || [];
  const companies = useMemo(
    () => workspaceContracts.map(toCompany),
    [workspaceContracts]
  );
  const activeWorkspace =
    workspaceContracts.find(item => item.id === account?.activeWorkspaceId) ||
    (workspaceContracts.length === 1 ? workspaceContracts[0] : null);
  const activeCompany = activeWorkspace ? toCompany(activeWorkspace) : null;
  const locale = pendingLocale || account?.user.locale || anonymousLocale;
  useWorkspaceIdentitySync({
    activeWorkspaceId: activeWorkspace?.id ?? null,
    workspaceContracts,
    companies,
    locale,
  });
  useEffect(() => {
    document.documentElement.lang = locale;
    if (i18n.resolvedLanguage !== locale) void i18n.changeLanguage(locale);
  }, [locale]);
  const value = useMemo<WorkspaceValue>(
    () => ({
      companies,
      workspaceContracts,
      activeCompany,
      activeWorkspace,
      projects: [],
      assets: [],
      locale,
      createCompany: async input => {
        const created = await api.request<WorkspaceContract>('/workspaces', {
          method: 'POST',
          body: JSON.stringify(input),
        });
        await refresh();
        return toCompany(created);
      },
      createInitialCompany: async input => {
        const created = await api.request<WorkspaceContract>(
          '/workspaces/onboarding',
          {
            method: 'POST',
            body: JSON.stringify(input),
          }
        );
        await refresh();
        return toCompany(created);
      },
      setActiveCompany: async id => {
        await api.request<{ ok: true }>(`/workspaces/${id}/activate`, {
          method: 'POST',
        });
        await refresh();
      },
      setLocale: async next => {
        const previous = locale;
        localStorage.setItem(LOCALE_KEY, next);
        setAnonymousLocale(next);
        setPendingLocale(account ? next : null);
        document.documentElement.lang = next;
        await i18n.changeLanguage(next);
        if (!account) return;
        try {
          const user = await api.request<AccountUser>('/auth/me', {
            method: 'PATCH',
            body: JSON.stringify({ locale: next }),
          });
          updateUser(user);
          setPendingLocale(null);
        } catch (error) {
          localStorage.setItem(LOCALE_KEY, previous);
          setAnonymousLocale(previous);
          setPendingLocale(null);
          document.documentElement.lang = previous;
          await i18n.changeLanguage(previous);
          throw error;
        }
      },
      refreshWorkspaces: refresh,
    }),
    [
      account,
      activeCompany,
      activeWorkspace,
      api,
      companies,
      locale,
      refresh,
      updateUser,
      workspaceContracts,
    ]
  );
  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceValue => {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('ActiveWorkspaceProvider is missing');
  return value;
};

export const useApiRequest = (): ReturnType<typeof useRegionalApi>['request'] =>
  useRegionalApi().request;

export { requestWithActiveRegionalClient as apiRequest };
